import { useEffect, useState } from "react";
import { RxCrossCircled } from "react-icons/rx";
import Tooltip from "@mui/material/Tooltip";
import toast from "react-hot-toast";
import api from "@/Api/Api";
import { useCache } from "@/Hooks/useCache";
import { Button } from "../../Button/Button";
import Modal from "@shared/Components/Modal/Modal";
import ModalButtons from "@shared/Components/Modal/ModalButtons";
import ModalContent from "@shared/Components/Modal/ModalContent";
import { AgentInvite } from "@shared/Models/Requests/AgentInvite";

interface PendingAgentInvitesModalProps {
	isOpen: boolean;
	closeModal(): void;
}

export function PendingAgentInvitesModal(props: PendingAgentInvitesModalProps) {
	const [pendingInvites, setPendingInvites] = useState<AgentInvite[]>();

	const cache = useCache();

	useEffect(() => {
		if (props.isOpen) {
			getPendingInvites();
		}

	}, [props.isOpen]);

	async function getPendingInvites() {
		const res = await api.BasicGet<AgentInvite[]>("/agent_invites/pending");
		if (res.successful && res.data != null) {
			setPendingInvites(res.data);
		} else {
			toast.error("Could not get the pending agent invites");
		}
	}

	function getTeamNameList(ids: number[]): string {
		return ids.map(id => cache.getTeam(id)?.name)
			.filter(name => name != null)
			.join(", ");
	}

	async function revokeInvite(email: string) {
		await api.BasicDelete("/agent_invites?email=" + email);

		getPendingInvites();
	}

	return (
		<Modal
			isOpen={props.isOpen}
			onClose={props.closeModal}
			title="Pending agent invites"
		>
			<ModalContent>
				{ pendingInvites != null && pendingInvites.length > 0 ?
					<div>
						<div className="grid grid-cols-10 grid-rows-1 gap-4 pl-4">
							<div className="col-span-3 font-semibold">Email</div>
							<div className="col-span-5 font-semibold">Teams</div>

							<div className="mr-4 font-semibold">Role</div>

							<div className="font-semibold">Cancel</div>
						</div>

						{pendingInvites.map(pendingInvite => (
							<div key={pendingInvite.email} className="border rounded-lg shadow-sm overflow-hidden p-4 my-4">
								<div className="grid grid-cols-10 grid-rows-1 gap-4">
									<div className="col-span-3 truncate pr-4">{pendingInvite.email}</div>
									<div className="col-span-5 truncate">{getTeamNameList(pendingInvite.teams)}</div>

									<div className=" px-4 col-span-1">
										{pendingInvite.isAdmin ? "Admin" : "User"}
									</div>
									<div className="pl-8 col-span-1">
										<Tooltip title="Cancels the pending invite">
											<button onClick={() => revokeInvite(pendingInvite.email)} className="text-red-700">
												<RxCrossCircled size={20} />
											</button>
										</Tooltip>
									</div>
								</div>
							</div>
						))}
					</div>

					:
					<div>
						<div className="text-lg font-medium">No active pending invites.</div>
					</div>
				}
			</ModalContent>

			<ModalButtons>
				<Button onClick={props.closeModal} label="Close" />
			</ModalButtons>
		</Modal>
	);
}
